/**
 * File validation utilities for dropzone
 * Implements comprehensive validation rules as per specification
 */

import { 
  FILE_TYPES_CONFIG, 
  isSupportedMimeType, 
  isImageType, 
  isVideoType,
  getMaxSizeForMimeType,
  getFileTypeByMimeType
} from './fileTypes';

// Layer 1 validation control
const LAYER1_VALIDATION_ENABLED = true;

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface FileValidationConfig {
  maxFiles: number;
  maxImageSize: number;
  maxVideoSize: number;
  allowedImageTypes: string[];
  allowedVideoTypes: string[];
  allowedSvgTypes: string[];
  imageWarningSize: number;
  videoWarningSize: number;
  svgWarningSize: number;
}

// Default configuration based on centralized file types
export const DEFAULT_VALIDATION_CONFIG: FileValidationConfig = {
  maxFiles: 100, // Early stage limit - will increase over time
  maxImageSize: FILE_TYPES_CONFIG.maxImageSize,
  maxVideoSize: FILE_TYPES_CONFIG.maxVideoSize,
  allowedImageTypes: FILE_TYPES_CONFIG.imageTypes,
  allowedVideoTypes: FILE_TYPES_CONFIG.videoTypes,
  allowedSvgTypes: ['image/svg+xml'], // Keep SVG separate for special handling
  imageWarningSize: 10 * 1024 * 1024, // 10MB
  videoWarningSize: 100 * 1024 * 1024, // 100MB
  svgWarningSize: 5 * 1024 * 1024, // 5MB
};

/**
 * Check if file has dangerous characters in filename
 */
export const hasSpecialCharacters = (filename: string): boolean => {
  const dangerousChars = /[<>:"|?*\\]/;
  return dangerousChars.test(filename);
};

/**
 * Check if file is hidden or system file
 */
export const isHiddenOrSystemFile = (filename: string): boolean => {
  return filename.startsWith('.') || filename.startsWith('~') || filename.includes('$');
};

/**
 * Check if file is zero-byte
 */
export const isZeroByteFile = (file: File): boolean => {
  return file.size === 0;
};

/**
 * Validate MIME type against file extension
 */
export const validateMimeType = (file: File): boolean => {
  const extension = file.name.toLowerCase().split('.').pop();
  const mimeType = file.type.toLowerCase();
  
  if (!extension) return false;
  
  // For SVG files, check extension and MIME type
  if (extension === 'svg') {
    const svgMimeTypes = [
      'image/svg+xml',
      'text/xml',
      'application/xml',
      '' // Some browsers don't set MIME type for SVG
    ];
    return svgMimeTypes.includes(mimeType);
  }
  
  // Use centralized configuration for MIME type validation
  if (!mimeType) return false;
  
  // Check if MIME type is supported
  if (!isSupportedMimeType(mimeType)) return false;
  
  // Get file type configuration and validate extension
  const fileTypeConfig = getFileTypeByMimeType(mimeType);
  if (!fileTypeConfig) return false;
  
  return fileTypeConfig.extensions.some(ext => 
    extension === ext.replace('.', '')
  );
};

/**
 * Check for basic file corruption by reading file headers
 */
export const checkFileCorruption = async (_file: File): Promise<boolean> => {
  return true;
};

/**
 * Validate SVG file for malicious content
 */
export const validateSvgContent = async (file: File): Promise<{ isValid: boolean; hasMaliciousContent: boolean; maliciousElements: string[] }> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      try {
        const content = reader.result as string;
        const maliciousElements: string[] = [];
        
        // Check for script tags
        if (/<script[^>]*>/i.test(content)) {
          maliciousElements.push('script tags');
        }
        
        // Check for event handlers
        const eventHandlers = [
          /onload=/i, /onerror=/i, /onclick=/i, /onmouseover=/i,
          /onfocus=/i, /onblur=/i, /onchange=/i, /onsubmit=/i
        ];
        
        eventHandlers.forEach(handler => {
          if (handler.test(content)) {
            maliciousElements.push('event handlers');
          }
        });
        
        // Check for javascript: URLs
        if (/javascript:/i.test(content)) {
          maliciousElements.push('javascript URLs');
        }
        
        // Check for foreignObject elements
        if (/<foreignObject[^>]*>/i.test(content)) {
          maliciousElements.push('foreign objects');
        }
        
        // Check for external references that could be malicious
        if (/href=["'](?!#)[^"']*["']/i.test(content)) {
          maliciousElements.push('external references');
        }
        
        const hasMaliciousContent = maliciousElements.length > 0;
        
        resolve({ 
          isValid: !hasMaliciousContent, 
          hasMaliciousContent, 
          maliciousElements: [...new Set(maliciousElements)] // Remove duplicates
        });
      } catch (error) {
        resolve({ isValid: false, hasMaliciousContent: true, maliciousElements: ['parsing error'] });
      }
    };
    
    reader.onerror = () => resolve({ isValid: false, hasMaliciousContent: true, maliciousElements: ['read error'] });
    reader.readAsText(file);
  });
};



/**
 * Generate file hash for duplicate detection
 */
export const generateFileHash = async (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = async () => {
      try {
        const arrayBuffer = reader.result as ArrayBuffer;
        const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        resolve(hashHex);
      } catch (error) {
        // Fallback to simple hash based on file properties
        const simpleHash = `${file.name}-${file.size}-${file.lastModified}`;
        const binaryString = unescape(encodeURIComponent(simpleHash));
        resolve(btoa(binaryString));
      }
    };
    
    reader.onerror = () => {
      const simpleHash = `${file.name}-${file.size}-${file.lastModified}`;
      const binaryString = unescape(encodeURIComponent(simpleHash));
      resolve(btoa(binaryString));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Comprehensive file validation
 */
export const validateFile = async (
  file: File, 
  config: FileValidationConfig = DEFAULT_VALIDATION_CONFIG
): Promise<ValidationResult> => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // Layer 1 Validation Control - Skip all validation if disabled
  if (!LAYER1_VALIDATION_ENABLED) {
    return { isValid: true, errors: [], warnings: ['Layer 1 validation disabled'] };
  }
  
  // 1. Check zero-byte files
  if (isZeroByteFile(file)) {
    errors.push(`⚠️ ${file.name} is empty and cannot be processed.`);
    return { isValid: false, errors, warnings };
  }

  // 2. Check hidden/system files
  if (isHiddenOrSystemFile(file.name)) {
    errors.push(`⚠️ System files like ${file.name} are not allowed and have been ignored.`);
    return { isValid: false, errors, warnings };
  }

  // 3. Check special characters in filename
  if (hasSpecialCharacters(file.name)) {
    errors.push(`⚠️ ${file.name} contains unsupported characters in its name. Please rename and try again.`);
    return { isValid: false, errors, warnings };
  }
  
  // 4. Check file type using centralized configuration
  const extension = file.name.toLowerCase().split('.').pop();
  const isSvg = config.allowedSvgTypes.includes(file.type) || extension === 'svg';
  
  // Use centralized file type validation
  const isSupportedType = isSupportedMimeType(file.type) || isSvg;
  
  if (!isSupportedType) {
    errors.push(`❌ Unsupported file type: ${file.name}. Only JPG, PNG, SVG, MP4, or MOV are allowed.`);
    return { isValid: false, errors, warnings };
  }
  
  const isImage = isImageType(file.type);
  const isVideo = isVideoType(file.type);
  
  // 5. Check MIME type consistency
  if (!validateMimeType(file)) {
    errors.push(`⚠️ ${file.name} has an invalid format. The file content does not match its extension.`);
    return { isValid: false, errors, warnings };
  }

  // 6. Check file size using centralized configuration
  let maxSize = 0;
  if (isSvg) {
    maxSize = config.maxImageSize; // SVG uses image size limit
  } else {
    // Use centralized max size configuration
    maxSize = getMaxSizeForMimeType(file.type);
    if (maxSize === 0) {
      // Fallback to config values if not found
      maxSize = isImage ? config.maxImageSize : config.maxVideoSize;
    }
  }

  if (file.size > maxSize) {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    if (isVideo) {
      errors.push(`❌ ${file.name} exceeds ${maxSizeMB} MB limit for videos. Please upload a smaller file.`);
    } else {
      errors.push(`❌ ${file.name} exceeds ${maxSizeMB} MB limit for images/SVG. Please upload a smaller file.`);
    }
    return { isValid: false, errors, warnings };
  }
  
  // Skipped file corruption check for testing purposes

  // 8. SVG specific validation
  if (isSvg) {
    try {
      const svgValidation = await validateSvgContent(file);
      if (svgValidation.hasMaliciousContent) {
        const elementsText = svgValidation.maliciousElements.join(', ');
        errors.push(`🚨 ${file.name} contains potentially dangerous content (${elementsText}). Please upload a safe SVG file.`);
        return { isValid: false, errors, warnings };
      }
      if (!svgValidation.isValid) {
        errors.push(`❌ ${file.name} is not a valid SVG file. Please check the file format.`);
        return { isValid: false, errors, warnings };
      }
    } catch (error) {
      warnings.push('Could not fully validate SVG content');
    }
  }
  
  // 10. Add size warnings
  if (isImage && file.size > config.imageWarningSize) {
    warnings.push('Large image files may take longer to process');
  }
  
  if (isVideo && file.size > config.videoWarningSize) {
    warnings.push('Large video files may take longer to process');
  }
  
  if (isSvg && file.size > config.svgWarningSize) {
    warnings.push('Large SVG files may take longer to process');
  }
  
  return { isValid: true, errors, warnings };
};

/**
 * Validate multiple files with batch limits
 */
export const validateFilesBatch = async (
  files: File[], 
  existingFiles: File[] = [],
  config: FileValidationConfig = DEFAULT_VALIDATION_CONFIG
): Promise<{
  validFiles: File[];
  invalidFiles: { file: File; errors: string[] }[];
  filesWithWarnings: { file: File; warnings: string[] }[];
  duplicateFiles: File[];
  batchErrors: string[];
}> => {
  // Layer 1 Validation Control - Skip all batch validation if disabled
  if (!LAYER1_VALIDATION_ENABLED) {
    return {
      validFiles: files,
      invalidFiles: [],
      filesWithWarnings: [],
      duplicateFiles: [],
      batchErrors: []
    };
  }
  
  const validFiles: File[] = [];
  const invalidFiles: { file: File; errors: string[] }[] = [];
  const filesWithWarnings: { file: File; warnings: string[] }[] = [];
  const duplicateFiles: File[] = [];
  const batchErrors: string[] = [];
  
  // Check batch size limit
  if (files.length + existingFiles.length > config.maxFiles) {
    batchErrors.push(`⚠️ Too many files. Only ${config.maxFiles} files per batch are allowed. Extra files have been ignored.`);
  }
  
  // Generate hashes for existing files for duplicate detection
  const existingHashes = new Set<string>();
  const existingNames = new Set<string>();
  
  for (const existingFile of existingFiles) {
    existingNames.add(existingFile.name);
    try {
      const hash = await generateFileHash(existingFile);
      existingHashes.add(hash);
    } catch (error) {
      // Continue without hash if generation fails
    }
  }
  
  // Validate each file
  for (const file of files) {
    // Check for name duplicates
    if (existingNames.has(file.name)) {
  // ...existing code...
      duplicateFiles.push(file);
      continue;
    }
    
    // Check for content duplicates
    try {
      const fileHash = await generateFileHash(file);
      if (existingHashes.has(fileHash)) {
  // ...existing code...
      duplicateFiles.push(file);
      continue;
    }
  // ...existing code...
      existingHashes.add(fileHash);
    } catch (error) {
      // Continue without hash check if generation fails
    }
    
    // Validate individual file
    const validation = await validateFile(file, config);
    
    if (!validation.isValid) {
  // ...existing code...
      invalidFiles.push({ file, errors: validation.errors });
    } else if (validation.warnings.length > 0) {
  // ...existing code...
      filesWithWarnings.push({ file, warnings: validation.warnings });
      validFiles.push(file);
    } else {
  // ...existing code...
      validFiles.push(file);
    }
    
    existingNames.add(file.name);
  }
  
  return {
    validFiles,
    invalidFiles,
    filesWithWarnings,
    duplicateFiles,
    batchErrors
  };
};